import { z } from 'zod';
import { extensionEntrySchema, publisherNameSchema, stripExtensionListUiPlaceholders, } from './extension-validators';
import { severitySchema } from './shared/severity';
import { extensionSecuritySettingsSchema } from './extension-security';
/*
  Generic policy contracts that can be reused for user policies and organization policies
*/
/*
  PolicyMode enum - the 3-way toggle state for each policy
*/
export var PolicyMode;
(function (PolicyMode) {
    PolicyMode["Off"] = "off";
    PolicyMode["Monitor"] = "monitor";
    PolicyMode["Enforce"] = "enforce";
})(PolicyMode || (PolicyMode = {}));
export const policyModeSchema = z.enum([
    PolicyMode.Off,
    PolicyMode.Monitor,
    PolicyMode.Enforce,
]);
/*
  PolicyKey enum - registry of all available policies

  ⚠️ ADDING OR CHANGING A POLICY? Update its detection-key classification in
  `apps/backend/src/domain/scans/detection-key.ts` → POLICY_INPUT_PROFILE
  (KIR-3973). That map decides when a policy's cached verdict is invalidated on
  a rules/config change; it is keyed `Record<PolicyKey, …>`, so a NEW policy is a
  compile error until you classify it. For a CHANGED policy (e.g. a new setting
  that affects detection), re-verify its `usesRuleset` / `usesOrgConfig` flags
  against the scanner — an over-narrow profile serves a STALE verdict. A tripwire
  test in `detection-key.test.ts` also fails when this enum changes.
*/
export var PolicyKey;
(function (PolicyKey) {
    PolicyKey["HiddenCharacters"] = "hidden-characters";
    PolicyKey["McpSecurity"] = "mcp-security";
    PolicyKey["CursorDatabase"] = "cursor-database";
    PolicyKey["YoloModes"] = "yolo-modes";
    PolicyKey["McpServerSources"] = "mcp-server-sources";
    PolicyKey["ExtensionSecurity"] = "extension-security";
    PolicyKey["ExtensionsManagement"] = "extensions-management";
    PolicyKey["AiInstructions"] = "ai-instructions";
    PolicyKey["SecretDetection"] = "secret-detection";
    PolicyKey["AiInstructionCatalog"] = "ai-instruction-catalog";
    PolicyKey["AiAgentInstructions"] = "ai-agent-instructions";
    // KIR-3925: scanner-less mode policy of the Threat Modeling catalog page.
    PolicyKey["ThreatModeling"] = "threat-modeling";
    PolicyKey["PiiDetection"] = "pii-detection";
})(PolicyKey || (PolicyKey = {}));
/**
 * Route param validation for `:policyKey`. Explicit tuple so `ai-instructions` is always
 * included (avoids occasional bundler/enum edge cases with `z.enum(PolicyKey)` alone).
 */
export const POLICY_KEY_VALUES = [
    PolicyKey.HiddenCharacters,
    PolicyKey.McpSecurity,
    PolicyKey.CursorDatabase,
    PolicyKey.YoloModes,
    PolicyKey.McpServerSources,
    PolicyKey.ExtensionSecurity,
    PolicyKey.ExtensionsManagement,
    PolicyKey.AiInstructions,
    PolicyKey.SecretDetection,
    PolicyKey.AiInstructionCatalog,
    PolicyKey.AiAgentInstructions,
    PolicyKey.ThreatModeling,
    PolicyKey.PiiDetection,
];
export const policyKeySchema = z.enum(POLICY_KEY_VALUES);
/**
 * Setting keys that are hidden from the policy settings UI and list API.
 * These are controlled by the Off/Monitor/Enforce selector instead.
 * Keep in sync when adding or renaming prevention-related settings.
 */
export const PREVENTION_SETTING_KEYS = {
    'extension-security': [
        'autoRemoveVulnerableExtensions',
        'removalSeverityThreshold',
    ],
    'extensions-management': ['autoRemoveBlockedExtensions'],
    'secret-detection': ['preventSecretLeakage'],
};
/*
  settingKeySchema validates a settingKey parameter.
  It must be a URL-safe string containing only alphanumeric characters, hyphens, and underscores.
  This ensures it's a single path element and prevents path traversal attacks.
  Example: "strictMode", "excessiveCommandsThreshold", "aiCodeSensitivity"
*/
export const settingKeySchema = z
    .string()
    .min(1, 'Setting key cannot be empty')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Setting key must be URL-safe (alphanumeric, hyphens, underscores only)');
/*
  Policy entity schemas
*/
/*
  Why the Enforce mode is unavailable for a policy, so the UI can show the
  correct tooltip:
  - 'feature_flag': the policy supports Enforce by design, but the
    enforce_policy_mode feature flag is off → "Contact Support to enable Enforce".
  - 'unsupported': the policy does not support Enforce by design → "Coming Soon".
  Absent when Enforce is available.
*/
export const ENFORCE_DISABLED_REASONS = {
    FeatureFlag: 'feature_flag',
    Unsupported: 'unsupported',
};
export const enforceDisabledReasonSchema = z.enum([
    ENFORCE_DISABLED_REASONS.FeatureFlag,
    ENFORCE_DISABLED_REASONS.Unsupported,
]);
export const policySchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    mode: policyModeSchema.optional(),
    supportsEnforce: z.boolean().optional(),
    enforceDisabledReason: enforceDisabledReasonSchema.optional(),
    enforceTooltip: z.string().optional(),
    usesSeverityThreshold: z.boolean().optional(),
    hasSettings: z.boolean(),
    settingsCount: z.number(),
    customizedSettings: z.number(),
    category: z.string(),
    settings: z.record(z.string(), z.any()).optional(),
    // KIR-3973 / KIR-3861: re-evaluation-freshness key (schema dk2). Changes when
    // re-processing the same content would differ — either a different finding set
    // (scanner code / ruleset / this policy's org config) OR a different
    // enforcement action (policy mode Off/Monitor/Enforce + org enforcement
    // severity threshold; secret-confidence for secret-detection). Because
    // enforcement is per-org, no policy's key is org-agnostic. Clients throw their
    // file cache when it changes so already-scanned files are re-evaluated.
    detectionKey: z.string().optional(),
    // KIR-3800 (Security Groups): on a GROUP-scoped policy list, `true` when this
    // policy's mode or settings are set at the group level (overriding the org
    // default), `false` when the group inherits the org value. Absent on
    // organization/account scopes (nothing to inherit from).
    isOverridden: z.boolean().optional(),
});
export const policyListSchema = z.object({
    policies: z.array(policySchema),
    // KIR-3973: one org-wide composite over every policy's detectionKey — a single
    // "did any detection input change?" signal to poll instead of diffing each key.
    compositeDetectionKey: z.string().optional(),
});
/*
  Policy toggle schemas
  Supports both legacy { enabled } and new { mode } for backward compatibility
*/
export const policyToggleSchema = z.object({
    enabled: z.boolean().optional(),
    mode: policyModeSchema.optional(),
});
export const policyToggleResultSchema = z.object({
    success: z.boolean(),
});
/*
  Policy settings schemas
*/
export const policySettingSchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    type: z.enum(['boolean', 'string', 'array', 'object']),
    defaultValue: z.any(),
    value: z.any(),
    isCustomized: z.boolean(),
    group: z.string().optional(),
    validation: z
        .object({
        pattern: z.string().optional(),
        minLength: z.number().optional(),
        maxLength: z.number().optional(),
        required: z.boolean().optional(),
        arrayItemType: z.enum(['string', 'object']).optional(),
        properties: z.record(z.string(), z.any()).optional(),
    })
        .optional(),
});
export const policySettingsSchema = z.object({
    policy: z.object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        supportsEnforce: z.boolean().optional(),
        enforceDisabledReason: enforceDisabledReasonSchema.optional(),
        enforceTooltip: z.string().optional(),
    }),
    settings: z.array(policySettingSchema),
});
/*
  Individual policy settings schemas
  Each policy has its own strict schema that prevents prototype pollution
*/
// YOLO Modes settings
const yoloModesSettingsSchema = z
    .object({
    // Cursor settings
    enableAutoRun: z.boolean().optional(),
    enableAutoFixDetection: z.boolean().optional(),
    enableAutoApplyDetection: z.boolean().optional(),
    // VSCode Copilot settings
    enableAgentAutoFix: z.boolean().optional(),
    enableGlobalAutoApprove: z.boolean().optional(),
    enableTerminalAutoApprove: z.boolean().optional(),
    enableMcpDiscovery: z.boolean().optional(),
    enableAutoAcceptDelay: z.boolean().optional(),
})
    .strict();
// MCP Server Sources settings
const mcpServerSourcesSettingsSchema = z
    .object({
    trustedGitRepositories: z.array(z.string()).optional(),
    trustedSources: z.array(z.string()).optional(),
    strictMode: z.boolean().optional(),
    allowLocalhostHttp: z.boolean().optional(),
    blockedListMode: policyModeSchema.optional(),
})
    .strict();
// MCP Security settings (combines dependencies and tool descriptions settings)
const mcpSecuritySettingsSchema = z
    .object({
    // Scan enablement flags
    enableHiddenCharactersScan: z.boolean().optional(),
    enableDependenciesScan: z.boolean().optional(),
    enableToolDescriptionsScan: z.boolean().optional(),
    // KIR-3157: scan MCP tool-call responses for hidden unicode.
    enableToolResponseScan: z.boolean().optional(),
    // Dependencies settings
    excludedPackages: z.array(z.string()).optional(),
    scanRecursiveDependencies: z.boolean().optional(),
    // Tool descriptions settings
    suspiciousPatterns: z
        .array(z.object({
        pattern: z.string(),
        reason: z.string(),
        severity: severitySchema,
    }))
        .optional(),
    enableAIToolAnalysis: z.boolean().optional(),
})
    .strict();
// Hidden Characters settings (no settings defined)
const hiddenCharactersSettingsSchema = z.object({}).strict();
// AI Rules / instructions policy settings
const aiInstructionsSettingsSchema = z
    .object({
    checkRules: z.boolean().optional(),
    checkSkills: z.boolean().optional(),
    checkCommands: z.boolean().optional(),
})
    .strict();
// Cursor Database settings (aligns with CURSOR_CHAT_SETTINGS)
// Pattern merging logic:
// - CODE BLOCKS: maliciousPatterns + credentialPatterns (merged in scanner)
// - COMMANDS: commandPatterns (with fallback to maliciousPatterns)
const cursorDatabaseSettingsSchema = z
    .object({
    // Primary pattern field for code block scanning
    maliciousPatterns: z
        .array(z.object({
        pattern: z.string(),
        reason: z.string().optional(), // tolerate legacy naming from frontend
        description: z.string().optional(),
        severity: severitySchema,
    }))
        .optional(),
    // Legacy credential patterns (merged with maliciousPatterns for code scanning)
    credentialPatterns: z
        .array(z.object({
        pattern: z.string(),
        reason: z.string().optional(), // tolerate legacy naming from frontend
        description: z.string().optional(),
        severity: severitySchema,
    }))
        .optional(),
    // Command-specific patterns
    commandPatterns: z
        .array(z.object({
        pattern: z.string(),
        reason: z.string().optional(), // tolerate legacy naming from frontend
        description: z.string().optional(),
        severity: severitySchema,
    }))
        .optional(),
    aiCodeSensitivity: severitySchema.optional(),
    enableCursorAIAnalysis: z.boolean().optional(),
})
    .strict();
const extensionsManagementExtensionListSchema = z.preprocess(stripExtensionListUiPlaceholders, z.array(extensionEntrySchema).optional());
// Extensions Management settings (allow/block lists are now in policy_exceptions)
const extensionsManagementSettingsSchema = z
    .object({
    autoRemoveBlockedExtensions: z.boolean().optional(),
    blockedListMode: policyModeSchema.optional(),
    // Legacy fields — migrated to policy_exceptions (migration 0071).
    // Retained so older agents that read settings don't break .strict() validation.
    defaultBehavior: z.string().optional(),
    allowedExtensions: extensionsManagementExtensionListSchema,
    blockedExtensions: extensionsManagementExtensionListSchema,
    allowedPublishers: z.array(publisherNameSchema).optional(),
    blockedPublishers: z.array(publisherNameSchema).optional(),
})
    .strict();
// Secret Detection settings (aligns with SECRET_DETECTION_SETTINGS)
const secretDetectionSettingsSchema = z
    .object({
    customPatterns: z
        .array(z
        .object({
        pattern: z.string(),
        severity: severitySchema,
        reason: z.string().optional(), // tolerate legacy naming from frontend
        description: z.string().optional(),
    })
        .transform(data => {
        // Map reason to description if description is missing (for backward compatibility)
        return {
            ...data,
            description: data.description ?? data.reason ?? '',
        };
    })
        .refine(data => data.description && data.description.length > 0, {
        message: 'Description is required',
    }))
        .optional(),
    preventSecretLeakage: z.boolean().optional(),
    secretFilePatterns: z
        .array(z.object({
        pattern: z.string(),
        severity: severitySchema,
        description: z.string(),
    }))
        .optional(),
})
    .strict();
// PII Detection settings (aligns with PII_DETECTION_SETTINGS; KIR-3034)
const piiDetectionSettingsSchema = z
    .object({
    // Built-in DLP infoTypes to inspect for (subset of the built-in set).
    infoTypes: z.array(z.string()).optional(),
    // Additional patterns -> DLP custom infoTypes.
    customPatterns: z
        .array(z.object({
        pattern: z.string(),
        severity: severitySchema,
        description: z.string(),
    }))
        .optional(),
    // DLP minLikelihood threshold.
    minLikelihood: z
        .enum(['VERY_UNLIKELY', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'VERY_LIKELY'])
        .optional(),
    // Which ingress sources to scan.
    scanPrompt: z.boolean().optional(),
    scanFileReads: z.boolean().optional(),
    scanMcpOutputs: z.boolean().optional(),
})
    .strict();
/*
  Policy settings update - discriminated union by policyKey
  This provides full type safety and automatic validation per policy
*/
export const policySettingsUpdateSchema = z.discriminatedUnion('policyKey', [
    z.object({
        policyKey: z.literal(PolicyKey.YoloModes),
        settings: yoloModesSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.ExtensionSecurity),
        settings: extensionSecuritySettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.McpSecurity),
        settings: mcpSecuritySettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.McpServerSources),
        settings: mcpServerSourcesSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.HiddenCharacters),
        settings: hiddenCharactersSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.CursorDatabase),
        settings: cursorDatabaseSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.AiInstructions),
        settings: aiInstructionsSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.SecretDetection),
        settings: secretDetectionSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.ExtensionsManagement),
        settings: extensionsManagementSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.PiiDetection),
        settings: piiDetectionSettingsSchema,
    }),
]);
export const policySettingsUpdateResultSchema = z.object({
    success: z.boolean(),
});
/*
  Policy settings delete result schema
*/
export const policySettingsDeleteResultSchema = z.object({
    success: z.boolean(),
});
/*
  Enforcement severity threshold schemas
  Global setting that controls at which severity level enforcement actions trigger
*/
export const enforcementSeverityThresholdSchema = z.object({
    severity: severitySchema,
});
export const enforcementSeverityThresholdResultSchema = z.object({
    success: z.boolean(),
});
/*
  Policy edit context schemas
  Used by the "Edit Policy" action on the Alerts screen
*/
export const policyEditContextQuerySchema = z.object({
    subPolicyKey: z.string().optional(),
    alertKey: z.string().optional(),
});
export const policyEditContextSchema = z.object({
    policy: z.object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        mode: policyModeSchema,
        supportsEnforce: z.boolean().optional(),
        enforceDisabledReason: enforceDisabledReasonSchema.optional(),
        enforceTooltip: z.string().optional(),
    }),
    subPolicy: z
        .object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        type: z.enum(['boolean', 'string', 'array', 'object']),
        defaultValue: z.any(),
        value: z.any(),
        isCustomized: z.boolean(),
    })
        .nullable(),
    version: z.number(),
});
/*
  Versioned policy toggle schema
  Extends policyToggleSchema with optional expectedVersion for conflict detection
*/
export const policyToggleVersionedSchema = z.object({
    enabled: z.boolean().optional(),
    mode: policyModeSchema.optional(),
    expectedVersion: z.number().optional(),
    source: z.enum(['policies', 'alerts']).optional(),
    alertKey: z.string().optional(),
});
/*
  Versioned policy settings update schema
  Adds optional expectedVersion and source metadata for conflict detection and auditing
*/
export const policySettingsUpdateVersionedMetaSchema = z.object({
    expectedVersion: z.number().optional(),
    source: z.enum(['policies', 'alerts']).optional(),
    alertKey: z.string().optional(),
});
//# sourceMappingURL=policies.js.map